import type { VerificationReport } from "@sequesign/lib/types";
import type { RunVerificationResult } from "../verify/run.js";

// Which completeness affordance, if any, to render below the result.
// "none" covers Case C (completeness passed; the report row is the
// affordance) and Case D (no result, the run errored, or the receipt
// failed verification; the failure is the message).
export type AffordanceCase = "A" | "B1" | "B2" | "none";

export interface AffordanceCaseInput {
  report: VerificationReport | null;
  completenessEnabled: boolean;
  witnessUrl: string;
  completenessKey: string;
  runError: string | null;
}

// Pure case determination, extracted from the component so it can be unit
// tested without a DOM. The verifier reports completeness_verified as
// null both when the check did not run and when it was attempted online
// but could not complete; the two are distinguished by report.completeness,
// which the verifier only attaches once the online query was attempted
// (including the error path). A positive contradiction surfaces as
// completeness_verified false, which the flag table already reports.
export function determineAffordanceCase(input: AffordanceCaseInput): AffordanceCase {
  const { report, completenessEnabled, witnessUrl, completenessKey, runError } = input;
  // Case D: nothing to add. No result yet, the run itself errored, or the
  // receipt failed verification (the failure is the message; completeness
  // would not help).
  if (!report) return "none";
  if (runError) return "none";
  if (!report.valid) return "none";
  // Case A: verified offline, completeness was not enabled.
  if (!completenessEnabled) return "A";
  // Case B1: enabled, but the witness URL or key is incomplete, so the
  // check could not have run.
  if (witnessUrl.trim() === "" || completenessKey.trim() === "") return "B1";
  // Both fields are filled, so the completeness check was attempted.
  // Case C: it passed. The completeness row in the report is the
  // affordance; nothing to add.
  if (report.flags.completeness_verified === true) return "none";
  // Case B2: attempted online but could not complete (401, 404, network
  // error). The verifier reports this as completeness_verified null with
  // a completeness section attached. A receipt with no witness-log
  // dimension has no completeness section and nothing to confirm, so it
  // gets no affordance.
  if (report.flags.completeness_verified === null && report.completeness !== undefined) {
    return "B2";
  }
  // completeness_verified false means the witness log positively
  // contradicts the receipt. That is a definitive finding already shown
  // as "Failed verification" in the flag table, so it is not duplicated
  // here. Anything else (no log dimension) is silent too.
  return "none";
}

// The verifier surfaces an online completeness error as a detail string
// "Completeness check could not run: <message>.". Reuse that text for the
// Case B2 reason rather than minting new error copy.
const COULD_NOT_RUN_PREFIX = "Completeness check could not run:";
export function completenessFailureReason(report: VerificationReport | null): string | null {
  if (!report?.details) return null;
  const detail = report.details.find((entry) => entry.startsWith(COULD_NOT_RUN_PREFIX));
  if (!detail) return null;
  return detail.slice(COULD_NOT_RUN_PREFIX.length).trim().replace(/\.$/, "");
}

interface CompletenessAffordanceProps {
  result: RunVerificationResult | null;
  completenessEnabled: boolean;
  witnessUrl: string;
  completenessKey: string;
  runError: string | null;
  onScrollToControls: () => void;
}

export function CompletenessAffordance({
  result,
  completenessEnabled,
  witnessUrl,
  completenessKey,
  runError,
  onScrollToControls
}: CompletenessAffordanceProps) {
  const report = result?.report ?? null;
  const affordanceCase = determineAffordanceCase({
    report,
    completenessEnabled,
    witnessUrl,
    completenessKey,
    runError
  });
  if (affordanceCase === "none") return null;

  let body: string;
  let buttonLabel: string;
  if (affordanceCase === "A") {
    body =
      "This verification ran offline. To confirm the receipt records every action the witness captured for this chain, enable the online completeness check.";
    buttonLabel = "Configure completeness check";
  } else if (affordanceCase === "B1") {
    body =
      "The online completeness check is enabled but the witness URL or API key is incomplete. Fill in both to confirm completeness against the witness log.";
    buttonLabel = "Edit completeness settings";
  } else {
    const reason = completenessFailureReason(report);
    body = reason
      ? `The completeness check could not run: ${reason}. The offline verification result above is still valid; completeness is an additional claim.`
      : "The completeness check could not run. The offline verification result above is still valid; completeness is an additional claim.";
    buttonLabel = "Edit completeness settings";
  }

  return (
    <div className="completeness-affordance">
      <p className="completeness-affordance-body">{body}</p>
      <button type="button" className="input-button" onClick={onScrollToControls}>
        {buttonLabel} ↑
      </button>
    </div>
  );
}
